/* The eight tabs that are a list of facts rather than a screen of their own.
 *
 * Each is deliberately honest about being empty. On a real synced tenant most of
 * these are blank, and "0 of 25 answered" with a way to fill them in reads as an
 * intake nobody has done yet, which is true. A blank panel with no explanation reads
 * as a broken product, which is not. */
import { useState } from 'react';
import type { Member, Visit, Scan } from '@acrostic/model';
import type { HistoryNote } from '@acrostic/model/records';
import { Panel, NoneRow } from '../../components/Panel.js';
import { useSource } from '../../data/SourceProvider.js';
import { useToast } from '../../components/Toast.js';
import { fmt } from '../../lib/fmt.js';

/* WHAT A MISSING LIST MEANS. Undefined is a source that does not send this kind of
   history at all; [] is a person who has none of it. Saying "none on record" for the
   first is a lie about the member, and the member is the one thing this screen must
   never be wrong about. */
function NotSent({ what }: { what: string }) {
  return (
    <NoneRow>
      The server does not send {what} yet. This works on the demo, and is on the list
      for the live app.
    </NoneRow>
  );
}

/** A label/value list where an absent value is a dash, never a zero or a blank. */
export function FieldList({ fields, answered }: {
  fields: [string, unknown][]; answered?: boolean;
}) {
  const filled = fields.filter(([, v]) => v != null && v !== '').length;
  return (
    <>
      {answered && (
        <div className="t-caption muted" style={{ marginBottom: 10 }}>
          {filled} of {fields.length} answered
        </div>
      )}
      <div className="fields">
        {fields.map(([k, v]) => (
          <div className="fieldrow" key={k}>
            <div className="t-caption muted">{k}</div>
            <div className="t-body-small">{v == null || v === '' ? '—' : String(v)}</div>
          </div>
        ))}
      </div>
    </>
  );
}

const SHOWS = 50;

export function Attendance(
  { m, visits, total }: { m: Member; visits: Visit[] | undefined; total: number | undefined },
) {
  /* THE REAL NUMBER, when the source sent one. `visits.length` is the length of a
     WINDOW - the server caps it at 200 - so a member of six years read "the most
     recent 50 of 200" when they had 1,240. Falls back to the array for a source
     that sends the whole history. */
  const all = total ?? visits?.length ?? 0;
  return (
    <Panel eyebrow="Most recent first" title="Attendance">
      {visits === undefined ? <NotSent what="a member’s visits" />
        : !visits.length ? <NoneRow>No visits on record.</NoneRow> : (
        <table className="table">
          <thead><tr className="t-label-small"><th>Date</th><th>Class</th><th>Coach</th><th>Status</th></tr></thead>
          <tbody>
            {visits.slice(0, SHOWS).map((v, i) => (
              <tr key={`${v.date}-${i}`}>
                <td className="t-body-small">{fmt.date(v.date)}</td>
                <td className="t-body-small muted">{v.className || '—'}</td>
                <td className="t-body-small muted">{v.coach ?? '—'}</td>
                <td className="t-body-small muted">{v.status ?? 'Attended'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {/* SAY WHAT IS BEING SHOWN. The table draws fifty and the source sends a
          window, so "every visit on record" was a claim neither of them makes. The
          two rates come off the member row rather than the history, so they are true
          whether or not the visits themselves were sent. */}
      <div className="t-caption muted" style={{ marginTop: 10 }}>
        {visits && all > SHOWS && `Showing the most recent ${SHOWS} of ${fmt.num(all)}. `}
        {fmt.num(m.visits30d)} in the last 30 days · {m.avgVisitsWk.toFixed(1)} a week
      </div>
    </Panel>
  );
}

/* SAY IT WHEN THE LIST IS A WINDOW. The server caps scans and notes at a hundred
   each, and a capped list that cannot say it is capped is a list quietly claiming to
   be everything - the same silence the lead board and the approvals queue were fixed
   for. `total` is undefined from a source that sends the whole history. */
function Capped({ shown, total }: { shown: number; total: number | undefined }) {
  if (total == null || total <= shown) return null;
  return (
    <div className="t-caption muted" style={{ marginTop: 10 }}>
      Showing the most recent {fmt.num(shown)} of {fmt.num(total)}.
    </div>
  );
}

export function Scans({ scans, total }: { scans: Scan[] | undefined; total: number | undefined }) {
  return (
    <Panel eyebrow="InBody" title="Scans">
      {scans === undefined ? <NotSent what="a member’s scans" />
        : !scans.length ? <NoneRow>No scans on record.</NoneRow> : (
        <table className="table">
          <thead><tr className="t-label-small">
            {/* PBF and SMM are InBody's own names: percent body fat and skeletal
                muscle mass. Printed as the machine prints them, because that is what
                the member is handed on the way out. */}
            <th>Date</th><th className="right">Weight</th><th className="right">PBF</th>
            <th className="right">SMM</th><th className="right">Score</th>
          </tr></thead>
          <tbody>
            {scans.map((s, i) => (
              <tr key={`${s.date}-${i}`}>
                <td className="t-body-small">{fmt.date(s.date)}</td>
                <td className="right t-body-small">{s.weight ?? '—'}</td>
                <td className="right t-body-small">{s.pbf ?? '—'}</td>
                <td className="right t-body-small">{s.smm ?? '—'}</td>
                <td className="right t-body-small">{s.score ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <Capped shown={scans?.length ?? 0} total={total} />
    </Panel>
  );
}

export function Sales({ m }: { m: Member }) {
  return (
    <Panel eyebrow="What they pay" title="Memberships &amp; sales">
      <FieldList fields={[
        ['Plan', m.plan],
        ['Price', m.priceCents == null ? null : fmt.money(m.priceCents)],
        ['Joined', m.joinedAt ? fmt.date(m.joinedAt) : null],
        ['Renews', m.membershipExpires ? fmt.date(m.membershipExpires) : null],
        ['Cancelled', m.cancelledAt ? fmt.date(m.cancelledAt) : null],
        ['Source', m.source],
      ]} />
      <div className="t-caption muted" style={{ marginTop: 10 }}>
        Purchase history comes from Mindbody and is not connected yet.
      </div>
    </Panel>
  );
}

export function Afp({ m }: { m: Member }) {
  /* The Athlete Fitness Plan intake. Empty on every synced tenant, which is a fact
     about the gym rather than about the software. */
  const f: [string, unknown][] = [
    ['Primary goal', m.goal], ['Why now', m.why],
    ['Target weight', null], ['Target body fat', null],
    ['100 day goal', null], ['12 month goal', null],
    ['Training history', null], ['Injuries', null],
    ['Days available', null], ['Preferred time', null],
  ];
  return (
    <Panel eyebrow="Intake" title="Fitness plan">
      <FieldList fields={f} answered />
      <div className="t-caption muted" style={{ marginTop: 10 }}>
        This is the intake a coach fills in with the member, not something a sync can import.
      </div>
    </Panel>
  );
}

export function Marketing({ m }: { m: Member }) {
  const f: [string, unknown][] = [
    ['Source', m.source], ['Source detail', m.sourceDetail],
    ['Referred by', null], ['Email opt in', null], ['SMS opt in', null],
    ['Last campaign', null], ['Opened', null], ['Replied', null],
  ];
  return (
    <Panel eyebrow="How they found you" title="Marketing">
      <FieldList fields={f} answered />
    </Panel>
  );
}

export function Details({ m }: { m: Member }) {
  /* The system record. On the shipped demo this tab renders 279 raw Salesforce
     labels, many of them FNS-branded, on every tenant. That is one gym's migration
     mirror and it does not belong on everybody's member record: here it is the
     identifiers this product actually holds. */
  const f: [string, unknown][] = [
    ['First name', m.firstName], ['Last name', m.lastName],
    ['Email', m.email], ['Phone', m.phone],
    ['Status', m.status], ['Member since', m.joinedAt ? fmt.date(m.joinedAt) : null],
    ['Support coach', m.supportCoach], ['Coach', m.coach],
    ['Mindbody id', m.externalIds.mindbodyId], ['InBody id', m.externalIds.inbodyId],
    ['Tags', m.tags.length ? m.tags.join(', ') : null],
  ];
  return (
    <Panel eyebrow="The system record" title="Details">
      <FieldList fields={f} />
    </Panel>
  );
}

/* WRITING ONE, which nothing could do either. A note is how the floor records the
   thing that is not a field — "asked after her mum", "shoulder still sore" — and it
   is what the prompt before a hold ends is built out of. A read-only notes tab is a
   record of what the import happened to carry and nothing since. */
function AddNote({ m, onChanged }: { m: Member; onChanged: () => void }) {
  const source = useSource();
  const toast = useToast();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);

  async function save() {
    const t = text.trim();
    if (!t) { toast('Nothing to write down yet', 'bad'); return; }
    setBusy(true);
    try {
      await source.addMemberNote(m.id, t);
      setText('');
      toast(`Noted on ${m.firstName}'s record`, 'good');
      onChanged();
    } catch (e) {
      toast((e as Error).message, 'bad');
    } finally { setBusy(false); }
  }

  return (
    <div className="addnote">
      <textarea
        className="field" rows={2} value={text}
        placeholder={`Something worth remembering about ${m.firstName}.`}
        onChange={(e) => setText(e.target.value)}
      />
      <div>
        <button
          type="button" className="btn t-label-small"
          disabled={busy || !text.trim()} onClick={() => void save()}
        >Add note</button>
      </div>
    </div>
  );
}

export function Notes(
  { m, notes, total, onChanged }: {
    m: Member;
    notes: HistoryNote[] | undefined;
    total: number | undefined;
    onChanged: () => void;
  },
) {
  return (
    <Panel eyebrow="What staff wrote down" title="Notes">
      <AddNote m={m} onChanged={onChanged} />
      {notes === undefined ? <NotSent what="a member’s notes" />
        : !notes.length ? <NoneRow>No notes yet.</NoneRow> : (
        <div className="rows">
          {notes.map((n, i) => (
            <div className="row" key={`${n.at}-${i}`}>
              <div className="who">
                <div className="n t-body-medium">{n.text}</div>
                {/* who is null when the system observed it rather than a person writing it
                  down. "System" is the truth; a blank reads as a missing name. */}
              <div className="s t-caption muted">{n.who ?? 'System'} · {fmt.date(n.at)}</div>
              </div>
            </div>
          ))}
          <Capped shown={notes?.length ?? 0} total={total} />
        </div>
      )}
    </Panel>
  );
}
